/** The Gate 1 / Gate 2 checklist standing between this contract and approval.
 *
 *  Shown whenever an earlier gate is still unsigned, so the reviewer sees which
 *  one and can go approve it, rather than filling in the decision form and
 *  getting a 409 back from review_gate_guard.py.
 */
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ListChecks, Check, Circle } from 'lucide-react';
import type { GatePrerequisite } from './gatePrerequisites';

export function GateSequenceBlockedMessage({ outstanding }: { outstanding: GatePrerequisite[] }) {
  const names = outstanding.map((p) => p.label).join(' and ');
  return (
    <p role="alert" className="text-xs text-destructive">
      {names} must be approved before this contract can be approved. You can still reject or
      escalate it now.
    </p>
  );
}

export function GateSequenceNotice({
  prerequisites,
}: {
  prerequisites: GatePrerequisite[];
}) {
  if (prerequisites.length === 0) return null;

  return (
    <Alert>
      <ListChecks className="h-4 w-4" aria-hidden="true" />
      <AlertDescription className="space-y-2">
        <p className="font-medium">
          Approving this contract requires the earlier gates to be signed first.
        </p>
        <ul className="space-y-1 text-xs">
          {prerequisites.map((p) => (
            <li key={p.stage} className="flex items-center gap-2">
              {p.approved ? (
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                  aria-hidden="true"
                />
              ) : (
                <Circle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <span className={p.approved ? '' : 'font-medium'}>{p.label}</span>
              <span className="text-muted-foreground">
                {p.approved
                  ? `approved${p.approved_at ? ` on ${new Date(p.approved_at).toLocaleDateString()}` : ''}`
                  : 'not yet approved'}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs">
          Open the outstanding gate's tab to approve it. Rejecting or escalating this contract
          does not require them.
        </p>
      </AlertDescription>
    </Alert>
  );
}
